import { useEffect, useId, useRef, useState } from "react";
import type { FormEvent } from "react";
import { PageHeader } from "@/components/PageHeader";
import { AreaTabs, BOOKS_TABS } from "@/components/AreaTabs";
import { StateMessage } from "@/components/StateMessage";
import { RowActions } from "@/components/RowActions";
import { VendorDialog } from "@/components/VendorDialog";
// ef-064-18 anchor: the per-row payee tax status + "Tax info" button (stub until ef-064-18).
import { VendorTaxControl } from "@/components/VendorTaxControl";
import { ApiError, demoApi, problemFieldErrors, problemMessage } from "@/api/client";
import { useVendors } from "@/hooks/useVendors";
import { useReferenceData } from "@/hooks/useReferenceData";
import { formatMoney } from "@/lib/format";
import type { Account, CreateDocumentRequest, Document, DocumentAging, Guid, Vendor } from "@/api/types";
import { DialogShell } from "@/components/DialogShell";
import "./BooksVendors.css";
import "./BooksDocuments.css";

/**
 * Books' Vendors tab (ef-042-6, `/books/vendors`) — list, create, rename and delete
 * against `ef-042-3`'s `/api/demo/vendors` write routes. A standalone top-level page,
 * same pattern `Places.tsx`/`JobCodes.tsx` use for their own Setup tab, rather than a
 * third branch inside `Books.tsx`'s own tab switch — this job's own file scope never
 * lists `Books.tsx`.
 *
 * ONE PRIMARY ACTION PER ROW (ADR 0009 §4): Edit stays directly on the row (the spec's
 * own row helper clicks it without opening a menu first); Delete — destructive, rarer,
 * and gated by a confirm dialog either way — sits behind `RowActions`' "More actions"
 * disclosure, same split `TimeApprovalQueue`'s own row uses between its one primary
 * button and everything else.
 *
 * ef-043-6 ADDS an Invoices/Bills section, same page rather than a sixth tab (ADR 0009
 * §2 caps an area at five; Reconcile already takes the fifth). Same one-primary-action
 * rule: "Record payment" is the row's own primary action while a document still has an
 * outstanding balance; "Void" — destructive, rarer — sits behind `RowActions` exactly
 * like the vendor row's own Delete above it.
 */
/** ef-048-2b — a fresh `Idempotency-Key` per submit, so a double-tapped Record
 *  payment/Void button replays the ORIGINAL response instead of acting twice. Same
 *  fallback-for-older-iOS-Safari shape `outbox.ts`'s own `newClientId` already documents;
 *  this key never leaves the device except as the header itself, so the (much smaller)
 *  collision odds on the fallback path are an acceptable trade against a hard runtime
 *  error. */
function newIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function BooksVendors() {
  const { vendors, status, error, create, update, remove } = useVendors();
  const { accounts, status: refStatus } = useReferenceData();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState<Vendor | null>(null);
  const [deleting, setDeleting] = useState<Vendor | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const listHeadingId = useId();
  const cancelBtnRef = useRef<HTMLButtonElement>(null);
  const confirmBtnRef = useRef<HTMLButtonElement>(null);
  const deleteTriggerRef = useRef<HTMLButtonElement | null>(null);

  // ---- ef-043-6 — invoices & bills state -----------------------------------------
  const [documents, setDocuments] = useState<Document[] | null>(null);
  const [docStatus, setDocStatus] = useState<"loading" | "ready" | "error">("loading");
  const [docError, setDocError] = useState<string | null>(null);
  const [docDialogOpen, setDocDialogOpen] = useState(false);

  // ef-049-0e — the aging strip's own three sums, server-side and unbounded by the
  // 200-row `getDocuments({ limit: 200 })` cap below. A separate fetch, not derived from
  // `documents` — the whole point (5112) is that the list's own 200-row cap can no longer
  // silently truncate what the strip shows.
  const [documentsAging, setDocumentsAging] = useState<DocumentAging | null>(null);

  const [payingId, setPayingId] = useState<Guid | null>(null);
  const [payAccountId, setPayAccountId] = useState("");
  const [payAmount, setPayAmount] = useState("");
  const [payBusy, setPayBusy] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);

  const [voidConfirmId, setVoidConfirmId] = useState<Guid | null>(null);
  const [voidBusy, setVoidBusy] = useState(false);
  const [voidError, setVoidError] = useState<string | null>(null);

  // ef-049-0a — synchronous in-flight guards for `confirmPay`/`confirmVoid`, additional
  // to the `payBusy`/`voidBusy` useState flags above (see those functions' own remarks).
  const payPostingRef = useRef(false);
  const voidPostingRef = useRef(false);

  const documentsHeadingId = useId();

  function openCreate(): void {
    setEditingVendor(null);
    setDialogOpen(true);
  }

  function openEdit(vendor: Vendor): void {
    setEditingVendor(vendor);
    setDialogOpen(true);
  }

  function closeDialog(): void {
    setDialogOpen(false);
  }

  function openDeleteConfirm(vendor: Vendor, trigger: HTMLButtonElement | null): void {
    deleteTriggerRef.current = trigger;
    setDeleting(vendor);
    setDeleteError(null);
  }

  function closeDeleteConfirm(): void {
    setDeleting(null);
    setDeleteError(null);
    deleteTriggerRef.current?.focus();
  }

  async function confirmDelete(): Promise<void> {
    if (!deleting) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await remove(deleting.id);
      setDeleting(null);
    } catch (err) {
      // The 409 path — VendorEndpoints.cs: "This vendor is still in use." — surfaced
      // verbatim rather than a generic failure, so a farmer knows to unwind an entry
      // first rather than retry the same delete.
      setDeleteError(problemMessage(err, "Could not delete this vendor."));
    } finally {
      setDeleteBusy(false);
    }
  }

  // ---- ef-043-6 — load documents once. Bounded (`limit`) rather than an unbounded
  // fetch — `rules/code-quality.md` §5 — even though this demo tenant's own document
  // volume never approaches it. ----------------------------------------------------
  useEffect(() => {
    const controller = new AbortController();

    async function load(): Promise<void> {
      setDocStatus("loading");
      setDocError(null);
      try {
        const docs = await demoApi.getDocuments({ limit: 200 }, controller.signal);
        if (controller.signal.aborted) return;
        setDocuments(docs);
        setDocStatus("ready");
      } catch (err) {
        if (controller.signal.aborted || (err instanceof DOMException && err.name === "AbortError")) return;
        setDocuments(null);
        setDocStatus("error");
        setDocError(problemMessage(err, "Could not load invoices and bills."));
      }
    }

    void load();
    return () => controller.abort();
  }, []);

  // ---- ef-049-0e — load the aging strip's own three sums, independent of the bounded
  // document list above. A failure here is silent (the strip's stats just stay at zero):
  // the document list itself, which has its own loading/error state below, is the primary
  // content of this section. ---------------------------------------------------------
  useEffect(() => {
    const controller = new AbortController();

    async function loadAging(): Promise<void> {
      try {
        const totals = await demoApi.getDocumentsAging(controller.signal);
        if (controller.signal.aborted) return;
        setDocumentsAging(totals);
      } catch (err) {
        if (controller.signal.aborted || (err instanceof DOMException && err.name === "AbortError")) return;
        setDocumentsAging(null);
      }
    }

    void loadAging();
    return () => controller.abort();
  }, []);

  async function handleCreateDocument(body: CreateDocumentRequest): Promise<Document> {
    const doc = await demoApi.createDocument(body);
    // Prepended rather than a full reload — same reasoning the settle/void handlers
    // below give: the create response IS a complete `Document`, so patching state
    // from it is strictly more current than a second round trip could be.
    setDocuments((prev) => (prev ? [doc, ...prev] : [doc]));
    return doc;
  }

  function openPay(id: Guid): void {
    setPayingId(id);
    setPayAccountId("");
    setPayAmount("");
    setPayError(null);
  }

  function cancelPay(): void {
    setPayingId(null);
    setPayError(null);
  }

  async function confirmPay(doc: Document): Promise<void> {
    // ef-049-0a — synchronous, ref-based in-flight guard: checked and set as the very
    // first statement, cleared in `finally`, so a SECOND same-tick click short-circuits
    // here before ever minting a new Idempotency-Key or calling the API, regardless of
    // whether React has re-rendered the `disabled` attribute yet. `payBusy` stays — it
    // still drives the visible `disabled` attribute; this ref is an ADDITIONAL guard.
    if (payPostingRef.current) return;
    payPostingRef.current = true;
    setPayBusy(true);
    setPayError(null);
    try {
      const result = await demoApi.settleDocument(
        doc.id,
        {
          cashAccountId: payAccountId,
          amount: Number(payAmount),
        },
        newIdempotencyKey(),
      );
      // The SAME row's own figure updates from the response, in place — no reload,
      // and never optimistic: a refused (409) settle leaves this untouched below.
      setDocuments(
        (prev) => prev?.map((d) => (d.id === doc.id ? { ...d, outstanding: result.outstanding } : d)) ?? prev,
      );
      setPayingId(null);
    } catch (err) {
      // A FIXED sentence, never the server's own restated dollar figure. The row's own
      // "outstanding" figure just above this form is already the single source of truth
      // for that number (`rules/... never sync the same data in two places`); echoing a
      // second, differently-formatted copy of it here is redundant at best and, on an
      // exact even-split amount, indistinguishable on screen from the row's own text.
      setPayError(
        err instanceof ApiError && err.status === 0
          ? "No connection — this didn't reach the farm office."
          : "Could not record this payment — it may be more than what's still owed.",
      );
    } finally {
      setPayBusy(false);
      payPostingRef.current = false;
    }
  }

  async function confirmVoid(doc: Document): Promise<void> {
    // ef-049-0a — same synchronous ref guard as `confirmPay` above.
    if (voidPostingRef.current) return;
    voidPostingRef.current = true;
    setVoidBusy(true);
    setVoidError(null);
    try {
      await demoApi.voidDocument(doc.id, newIdempotencyKey());
      setDocuments((prev) => prev?.filter((d) => d.id !== doc.id) ?? prev);
      setVoidConfirmId(null);
    } catch (err) {
      setVoidError(problemMessage(err, "Could not void this document."));
    } finally {
      setVoidBusy(false);
      voidPostingRef.current = false;
    }
  }

  const count = vendors?.length ?? 0;
  const vendorList = vendors ?? [];
  const vendorsById = new Map(vendorList.map((v) => [v.id, v.name] as const));
  const incomeAccounts = accounts.filter((a) => a.kind === "Income");
  const expenseAccounts = accounts.filter((a) => a.kind === "Expense");
  const assetAccounts = accounts.filter((a) => a.kind === "Asset");

  const todayIso = new Date().toISOString().slice(0, 10);
  const docList = documents ?? [];
  // ef-049-0e — read from the server-side aggregate (`documentsAging`), never re-derived
  // from `docList` here: that list is capped at 200 rows (`getDocuments({ limit: 200 })`
  // above) and would silently undercount past it. Zero while `documentsAging` hasn't
  // loaded yet — the strip itself only renders once `docList` is non-empty (below).
  const arTotal = documentsAging?.arTotal ?? 0;
  const apTotal = documentsAging?.apTotal ?? 0;
  const overdueTotal = documentsAging?.overdueTotal ?? 0;

  return (
    <main className="books-vendors-page">
      <PageHeader
        title="Vendors"
        subtitle="Who you pay — created once here, then picked on every Entry and Register write."
        action={
          <button type="button" className="btn btn--primary" onClick={openCreate}>
            New vendor
          </button>
        }
      />

      <AreaTabs area="/books" tabs={BOOKS_TABS} label="Books" />

      {/* Ground truth for a tester, rules/ui-breadcrumbs.md §1 — read off the DOM rather
          than inferred from sentence text. */}
      <section
        className="books-vendors"
        aria-labelledby={listHeadingId}
        data-state={status === "ready" && count === 0 ? "empty" : status}
      >
        <h2 id={listHeadingId} className="books-vendors__heading">
          Your vendors
        </h2>

        {status === "loading" && <StateMessage tone="loading" title="Loading vendors…" />}

        {status === "error" && (
          <StateMessage tone="error" title="Could not load vendors." detail={error ?? undefined} />
        )}

        {status === "ready" && count === 0 && (
          <StateMessage
            tone="empty"
            title="No vendors yet."
            detail="Add who you pay, and they'll show up as a pick on every Entry and Register write."
          />
        )}

        {status === "ready" && vendors !== null && count > 0 && (
          <ul className="books-vendors__list" aria-labelledby={listHeadingId}>
            {vendors.map((vendor) => (
              <li key={vendor.id} className="books-vendors__row" data-vendor-id={vendor.id}>
                <span className="books-vendors__name">{vendor.name}</span>
                {/* ef-064-18 anchor - the payee tax status and "Tax info" (ef-064-18 REPLACES the component; this line stays). */}
                <VendorTaxControl vendor={vendor} />
                <span className="books-vendors__row-actions">
                  <button type="button" className="btn btn--ghost" onClick={() => openEdit(vendor)}>
                    Edit
                  </button>
                  <RowActions label="More actions">
                    <button
                      type="button"
                      className="btn btn--ghost books-vendors__delete"
                      onClick={(e) => openDeleteConfirm(vendor, e.currentTarget)}
                    >
                      Delete
                    </button>
                  </RowActions>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ef-043-6 — Invoices & bills, the AR/AP half of Books' Vendors tab. Same
          ground-truth `data-state` discipline as the vendors section above. */}
      <section
        className="books-documents"
        aria-labelledby={documentsHeadingId}
        data-state={docStatus === "ready" && docList.length === 0 ? "empty" : docStatus}
      >
        <div className="books-documents__header">
          <h2 id={documentsHeadingId} className="books-documents__heading">
            Invoices & bills
          </h2>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => setDocDialogOpen(true)}
            disabled={refStatus !== "ready"}
          >
            New invoice / bill
          </button>
        </div>

        {docStatus === "loading" && <StateMessage tone="loading" title="Loading invoices and bills…" />}

        {docStatus === "error" && (
          <StateMessage
            tone="error"
            title="Could not load invoices and bills."
            detail={docError ?? undefined}
          />
        )}

        {docStatus === "ready" && docList.length === 0 && (
          <StateMessage
            tone="empty"
            title="No invoices or bills yet."
            detail="Create one to start tracking what you're owed and what you owe."
          />
        )}

        {docStatus === "ready" && docList.length > 0 && (
          <>
            <div className="books-documents__aging" role="group" aria-label="AR and AP aging summary">
              <div className="books-documents__aging-stat">
                <span className="books-documents__aging-label">Outstanding AR</span>
                <span className="books-documents__aging-amount">{formatMoney(arTotal)}</span>
              </div>
              <div className="books-documents__aging-stat">
                <span className="books-documents__aging-label">Outstanding AP</span>
                <span className="books-documents__aging-amount">{formatMoney(apTotal)}</span>
              </div>
              <div className="books-documents__aging-stat books-documents__aging-stat--overdue">
                <span className="books-documents__aging-label">Overdue</span>
                <span className="books-documents__aging-amount">{formatMoney(overdueTotal)}</span>
              </div>
            </div>

            <ul className="books-documents__list" aria-labelledby={documentsHeadingId}>
              {docList.map((doc) => {
                const isOverdue = doc.outstanding > 0 && doc.dueOn !== null && doc.dueOn < todayIso;
                const counterpartyName = doc.counterpartyId
                  ? (vendorsById.get(doc.counterpartyId) ?? "Unknown vendor")
                  : "No counterparty";

                return (
                  <li key={doc.id} className="books-documents__row" data-document-id={doc.id}>
                    <div className="books-documents__row-main">
                      <span
                        className={`books-documents__kind-badge books-documents__kind-badge--${doc.kind}`}
                      >
                        {doc.kind === "invoice" ? "Invoice" : "Bill"}
                      </span>
                      <span className="books-documents__counterparty">{counterpartyName}</span>
                      {isOverdue && <span className="books-documents__overdue-badge">Overdue</span>}
                    </div>

                    <div className="books-documents__row-amounts">
                      {/* ONE flat text node in ONE element, not two — a partial payment can
                          coincidentally split a face amount exactly in half, and a Playwright
                          text locator scoped to the row would treat two SEPARATE elements that
                          both happen to carry that same figure as a strict-mode violation, not
                          two independent matches. No nested element here for that reason. */}
                      <span className="books-documents__outstanding-amount">
                        {doc.outstanding !== doc.amount
                          ? `${formatMoney(doc.outstanding)} outstanding — ${formatMoney(
                              doc.amount - doc.outstanding,
                            )} paid of ${formatMoney(doc.amount)} total`
                          : `${formatMoney(doc.outstanding)} outstanding`}
                      </span>
                    </div>

                    <div className="books-documents__row-actions">
                      {doc.outstanding > 0 &&
                        (payingId === doc.id ? (
                          <div role="group" aria-label="Record payment" className="books-documents__pay-form">
                            <div className="document-dialog__field">
                              <label htmlFor={`pay-account-${doc.id}`}>Account</label>
                              <select
                                id={`pay-account-${doc.id}`}
                                value={payAccountId}
                                onChange={(e) => setPayAccountId(e.target.value)}
                              >
                                <option value="">Choose an account</option>
                                {assetAccounts.map((a) => (
                                  <option key={a.id} value={a.id}>
                                    {a.number} — {a.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="document-dialog__field">
                              <label htmlFor={`pay-amount-${doc.id}`}>Amount</label>
                              <input
                                id={`pay-amount-${doc.id}`}
                                type="text"
                                inputMode="decimal"
                                className="document-dialog__amount-input"
                                value={payAmount}
                                onChange={(e) => setPayAmount(e.target.value)}
                              />
                            </div>
                            {payError && (
                              <p className="books-documents__pay-error" role="alert">
                                {payError}
                              </p>
                            )}
                            <div className="books-documents__pay-actions">
                              <button
                                type="button"
                                className="btn btn--ghost"
                                onClick={cancelPay}
                                disabled={payBusy}
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                className="btn btn--primary"
                                onClick={() => void confirmPay(doc)}
                                disabled={payBusy || payAccountId === "" || !(Number(payAmount) > 0)}
                              >
                                {payBusy ? "Recording…" : "Confirm payment"}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button type="button" className="btn btn--primary" onClick={() => openPay(doc.id)}>
                            Record payment
                          </button>
                        ))}

                      <RowActions label="More actions">
                        {voidConfirmId === doc.id ? (
                          <div className="books-documents__void-confirm">
                            <p>Void this document? It cannot be recovered.</p>
                            {voidError && (
                              <p className="books-documents__pay-error" role="alert">
                                {voidError}
                              </p>
                            )}
                            <button
                              type="button"
                              className="btn btn--ghost"
                              onClick={() => setVoidConfirmId(null)}
                              disabled={voidBusy}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              className="btn btn--danger"
                              onClick={() => void confirmVoid(doc)}
                              disabled={voidBusy}
                            >
                              {voidBusy ? "Voiding…" : "Confirm void"}
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="btn btn--ghost books-documents__void"
                            onClick={() => setVoidConfirmId(doc.id)}
                          >
                            Void
                          </button>
                        )}
                      </RowActions>
                    </div>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </section>

      {dialogOpen && (
        <VendorDialog vendor={editingVendor} onClose={closeDialog} onCreate={create} onUpdate={update} />
      )}

      {docDialogOpen && (
        <DocumentDialog
          incomeAccounts={incomeAccounts}
          expenseAccounts={expenseAccounts}
          vendors={vendorList}
          onClose={() => setDocDialogOpen(false)}
          onCreate={handleCreateDocument}
        />
      )}

      {deleting && (
        <DialogShell
          title={<>Delete {deleting.name}?</>}
          titleId="delete-vendor-title"
          role="alertdialog"
          size="sm"
          onClose={closeDeleteConfirm}
          busy={deleteBusy}
          initialFocus={cancelBtnRef}
          describedBy="delete-vendor-desc"
          footer={
            <>
              <button
                ref={cancelBtnRef}
                type="button"
                className="btn btn--ghost"
                onClick={closeDeleteConfirm}
                disabled={deleteBusy}
              >
                Cancel
              </button>
              <button
                ref={confirmBtnRef}
                type="button"
                className="btn btn--danger"
                onClick={() => void confirmDelete()}
                disabled={deleteBusy}
              >
                {deleteBusy ? "Deleting…" : "Delete"}
              </button>
            </>
          }
        >
          <p id="delete-vendor-desc" className="books-vendors__dialog-desc">
            This removes {deleting.name} for good — a vendor has no archived state to
            restore it from. If any entry still references it, the server refuses and
            says so below; nothing is removed halfway.
          </p>
          {deleteError && (
            <p className="books-vendors__dialog-error" role="alert">
              {deleteError}
            </p>
          )}
        </DialogShell>
      )}
    </main>
  );
}

/**
 * ef-043-6 — the "New invoice / bill" dialog `BooksVendors.tsx` opens from the
 * Invoices & bills header action. Same overlay/focus-trap shape as
 * `RecurringTemplateDialog.tsx`/`VendorDialog.tsx`.
 *
 * `Account` IS KIND-FILTERED (income for an invoice, expense for a bill) rather than one
 * flat picker — `CreateDocumentRequest`'s own remarks: the server 400s `accountId` on a
 * kind mismatch, and filtering here means that round trip never has to happen to find out.
 * Not exported: this job's own file scope is `BooksVendors.tsx` + CSS only, so every
 * component this section needs lives in this one file rather than a new component file.
 */
function DocumentDialog({
  incomeAccounts,
  expenseAccounts,
  vendors,
  onClose,
  onCreate,
}: {
  incomeAccounts: Account[];
  expenseAccounts: Account[];
  vendors: Vendor[];
  onClose: () => void;
  onCreate: (body: CreateDocumentRequest) => Promise<Document>;
}) {
  const [kind, setKind] = useState<"invoice" | "bill">("invoice");
  const [counterpartyId, setCounterpartyId] = useState("");
  const [accountId, setAccountId] = useState("");
  const [amount, setAmount] = useState("");
  const [incurredOn, setIncurredOn] = useState(() => new Date().toISOString().slice(0, 10));
  const [dueOn, setDueOn] = useState("");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]> | null>(null);

  const titleId = useId();
  const kindId = useId();
  const counterpartyFieldId = useId();
  const accountFieldId = useId();
  const amountId = useId();
  const incurredId = useId();
  const dueId = useId();
  const formId = useId();

  const categoryAccounts = kind === "invoice" ? incomeAccounts : expenseAccounts;
  const amountNum = Number(amount);
  const canSubmit = accountId !== "" && incurredOn !== "" && amount.trim() !== "" && amountNum > 0;

  function switchKind(next: "invoice" | "bill"): void {
    setKind(next);
    // The previous selection may not even exist in the new kind's own account list —
    // clearing it forces a fresh, valid pick rather than silently submitting a stale one.
    setAccountId("");
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setFormError(null);
    setFieldErrors(null);
    if (!canSubmit) return;

    setSaving(true);
    try {
      await onCreate({
        kind,
        accountId,
        amount: amountNum,
        incurredOn,
        ...(dueOn ? { dueOn } : {}),
        ...(counterpartyId ? { counterpartyId } : {}),
      });
      onClose();
    } catch (err) {
      setFieldErrors(problemFieldErrors(err));
      setFormError(problemMessage(err, "Could not save this document."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <DialogShell
      title="New invoice / bill"
      titleId={titleId}
      onClose={onClose}
      busy={saving}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" form={formId} className="btn btn--primary" disabled={saving || !canSubmit}>
            {saving ? "Saving…" : "Save document"}
          </button>
        </>
      }
    >
      <form id={formId} className="document-dialog__form" onSubmit={(e) => void handleSubmit(e)} noValidate>
        <div className="document-dialog__field">
          <label htmlFor={kindId}>Kind</label>
          <select id={kindId} value={kind} onChange={(e) => switchKind(e.target.value as "invoice" | "bill")}>
            <option value="invoice">Invoice</option>
            <option value="bill">Bill</option>
          </select>
        </div>

        <div className="document-dialog__field">
          <label htmlFor={counterpartyFieldId}>Counterparty</label>
          <select
            id={counterpartyFieldId}
            value={counterpartyId}
            onChange={(e) => setCounterpartyId(e.target.value)}
          >
            <option value="">No counterparty</option>
            {vendors.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
        </div>

        <div className="document-dialog__field">
          <label htmlFor={accountFieldId}>Account</label>
          <select
            id={accountFieldId}
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            aria-invalid={fieldErrors?.accountId ? true : undefined}
          >
            <option value="">Choose an account</option>
            {categoryAccounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.number} — {a.name}
              </option>
            ))}
          </select>
          {fieldErrors?.accountId && (
            <p className="document-dialog__field-error" role="alert">
              {fieldErrors.accountId[0]}
            </p>
          )}
        </div>

        <div className="document-dialog__field">
          <label htmlFor={amountId}>Amount</label>
          <input
            id={amountId}
            type="text"
            inputMode="decimal"
            className="document-dialog__amount-input"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            aria-invalid={fieldErrors?.amount ? true : undefined}
          />
          {fieldErrors?.amount && (
            <p className="document-dialog__field-error" role="alert">
              {fieldErrors.amount[0]}
            </p>
          )}
        </div>

        <div className="document-dialog__field">
          <label htmlFor={incurredId}>Incurred on</label>
          <input id={incurredId} type="date" value={incurredOn} onChange={(e) => setIncurredOn(e.target.value)} />
        </div>

        <div className="document-dialog__field">
          <label htmlFor={dueId}>Due on</label>
          <input id={dueId} type="date" value={dueOn} onChange={(e) => setDueOn(e.target.value)} />
        </div>

        {formError && !fieldErrors?.accountId && !fieldErrors?.amount && (
          <p className="document-dialog__form-error" role="alert">
            {formError}
          </p>
        )}

      </form>
    </DialogShell>
  );
}
